// Background registry — metadata only. No background components are imported
// here, so this file has zero bundle cost. The `load` function is a dynamic
// import used by React.lazy() only when a background is actually selected.
//
// Every theme is a portrait-native ambient backdrop (see STYLE_GUIDE.md and
// engine/AmbientBackground.jsx). The `preview` gradient mirrors each theme's
// default colour set so the picker swatch matches the live result.

export const BACKGROUNDS = [
  {
    id: "celebration",
    label: "Golden Celebration",
    preview: "radial-gradient(135% 100% at 50% 0%, #fff7e0 0%, #f3d27a 27%, #c9824a 58%, #59283d 100%)",
    sets: [
      { id: "champagne-gold", label: "Champagne Gold" },
      { id: "rose-blush",     label: "Rose Blush"     },
      { id: "midnight-gala",  label: "Midnight Gala"  },
    ],
    defaultSetId: "champagne-gold",
    load: () => import("./celebration/CelebrationBackground.jsx"),
  },
  {
    id: "wedding",
    label: "Wedding",
    preview: "radial-gradient(135% 100% at 50% 0%, #fbf7ee 0%, #eef0df 26%, #b9c7a8 62%, #5d7459 100%)",
    sets: [
      { id: "ivory-sage",       label: "Ivory & Sage"      },
      { id: "champagne-pearl",  label: "Champagne Pearl"   },
      { id: "midnight-romance", label: "Midnight Romance"  },
    ],
    defaultSetId: "ivory-sage",
    load: () => import("./wedding/WeddingBackground.jsx"),
  },
  {
    id: "botanical",
    label: "Botanical",
    preview: "radial-gradient(130% 100% at 50% 4%, #fbeef0 0%, #f2d3d8 26%, #b9c69e 64%, #4e6a48 100%)",
    sets: [
      { id: "blush-garden",  label: "Blush Garden"  },
      { id: "sage-morning",  label: "Sage Morning"  },
      { id: "lavender-dusk", label: "Lavender Dusk" },
    ],
    defaultSetId: "blush-garden",
    load: () => import("./botanical/BotanicalBackground.jsx"),
  },
  {
    id: "aurora",
    label: "Aurora Night",
    preview: "radial-gradient(140% 110% at 50% 100%, #1c2a5a 0%, #1a2348 36%, #141a38 68%, #0b0e22 100%)",
    sets: [
      { id: "northern-violet", label: "Northern Violet" },
      { id: "emerald-veil",    label: "Emerald Veil"    },
      { id: "rose-quartz",     label: "Rose Quartz"     },
    ],
    defaultSetId: "northern-violet",
    load: () => import("./aurora/AuroraBackground.jsx"),
  },
  {
    id: "clinical",
    label: "Clinical Pulse",
    preview: "radial-gradient(135% 105% at 50% 0%, #f1faf8 0%, #d7efe9 30%, #8fc6bd 64%, #3a6e66 100%)",
    sets: [
      { id: "teal-clean",    label: "Teal Clean"    },
      { id: "midnight-ward", label: "Midnight Ward" },
      { id: "warm-care",     label: "Warm Care"     },
    ],
    defaultSetId: "teal-clean",
    load: () => import("./clinical/ClinicalBackground.jsx"),
  },
  {
    id: "geometric",
    label: "Geometric Summit",
    preview: "radial-gradient(140% 110% at 50% 0%, #21314f 0%, #1c2a44 36%, #172234 70%, #0d141f 100%)",
    sets: [
      { id: "slate-azure",    label: "Slate Azure"    },
      { id: "graphite-amber", label: "Graphite Amber" },
      { id: "indigo-mint",    label: "Indigo Mint"    },
    ],
    defaultSetId: "slate-azure",
    load: () => import("./geometric/GeometricBackground.jsx"),
  },
  {
    id: "royal",
    label: "Royal Indian",
    preview: "radial-gradient(135% 100% at 50% 0%, #fff3cf 0%, #f0c24f 24%, #b3431f 60%, #4a0d12 100%)",
    sets: [
      { id: "maharaja-gold",    label: "Maharaja Gold"    },
      { id: "peacock-saffron",  label: "Peacock Saffron"  },
      { id: "mughal-heritage",  label: "Mughal Heritage"  },
      { id: "celestial-temple", label: "Celestial Temple" },
    ],
    defaultSetId: "maharaja-gold",
    load: () => import("./royal/RoyalBackground.jsx"),
  },
  {
    id: "hospital",
    label: "Hospital & Rescue",
    preview: "radial-gradient(135% 105% at 50% 0%, #f2fbf7 0%, #d3efe4 30%, #7bbfa8 64%, #2f6b58 100%)",
    sets: [
      { id: "scrub-green",   label: "Scrub Green"   },
      { id: "ambulance-red", label: "Ambulance Red" },
      { id: "night-ot",      label: "Night OT"      },
      { id: "fantasy-realm", label: "Fantasy Realm" },
    ],
    defaultSetId: "scrub-green",
    load: () => import("./hospital/HospitalBackground.jsx"),
  },
  {
    id: "jewels",
    label: "Bejewelled Gold",
    preview: "radial-gradient(135% 100% at 50% 0%, #fff6da 0%, #f0cf72 24%, #9c5a2a 60%, #2a0e14 100%)",
    sets: [
      { id: "royal-gold",         label: "Royal Gold"         },
      { id: "velvet-casket",      label: "Velvet Casket"      },
      { id: "enchanted-treasure", label: "Enchanted Treasure" },
      { id: "dragons-hoard",      label: "Dragon's Hoard"     },
    ],
    defaultSetId: "royal-gold",
    load: () => import("./jewels/JewelsBackground.jsx"),
  },
];

const byId = Object.fromEntries(BACKGROUNDS.map((b) => [b.id, b]));

export function getBackground(id) {
  return byId[id] ?? null;
}
